import type redux from 'redux';
import { datadogRum } from '@datadog/browser-rum';
import type { PractitionerRole } from '@common/enums/practitionerRole';
import type { ServicePermissions } from '@common/interfaces';
import type { Profession } from '../../../common/enums/profession';
import type { IManagerRole } from '../../../common/interfaces/managerRole';
import { setCsrfToken } from '../utils/csrfToken';
import { AuthenticationType } from './actions';

export interface IAuthenticationState {
    areContactsAndTasksEnabled?: boolean;
    areEmploymentTaxesDetailsRequired?: boolean;
    emailAddress?: string;
    employmentPractitionerRoles?: PractitionerRole[];
    failed: boolean;
    groupManagerId?: string;
    hasEmploymentRights?: boolean;
    hasKsyosEmployment?: boolean;
    id?: string;
    isChecking: boolean;
    isGeneralPractitioner?: boolean;
    isGroupManager?: boolean;
    isKsyos?: boolean;
    isManager?: boolean;
    isSupervisor?: boolean;
    isSystemManager?: boolean;
    loggedIn: boolean;
    loggingIn: boolean;
    managerRole?: IManagerRole | null;
    nameFamily?: string;
    nameGiven?: string | null;
    nameInfix?: string | null;
    needsSupervisor?: boolean;
    organizationHasGeneralPractitionerOrSupervisor?: boolean;
    organizationId?: string;
    organizationType?: string;
    profession?: Profession;
    profilePictureUrl?: string;
    selectedEmploymentId?: string;
    servicePermissions?: ServicePermissions;
    token?: string;
}

interface IAuthenticationAction {
    areEmploymentTaxesDetailsRequired: boolean;
    profilePictureUrl: string;
    type: AuthenticationType;
    user: IAuthenticationState;
}

function authentication(
    state: IAuthenticationState = { loggedIn: false, loggingIn: false, failed: false, isChecking: true },
    action: IAuthenticationAction
): IAuthenticationState {
    switch (action.type) {
        case AuthenticationType.authenticationLoggedIn:
            if (action.user.token) {
                setCsrfToken(action.user.token);
            }
            datadogRum.setUser({
                id: action.user.id,
                isKsyos: action.user.isKsyos,
                organizationId: action.user.organizationId,
                profession: action.user.profession,
            });

            return {
                areContactsAndTasksEnabled: action.user.areContactsAndTasksEnabled,
                areEmploymentTaxesDetailsRequired: action.user.areEmploymentTaxesDetailsRequired,
                emailAddress: action.user.emailAddress,
                employmentPractitionerRoles: action.user.employmentPractitionerRoles,
                failed: false,
                groupManagerId: action.user.groupManagerId,
                hasEmploymentRights: action.user.hasEmploymentRights,
                hasKsyosEmployment: action.user.hasKsyosEmployment,
                id: action.user.id,
                isChecking: false,
                isGeneralPractitioner: action.user.isGeneralPractitioner,
                isGroupManager: action.user.isGroupManager,
                isKsyos: action.user.isKsyos,
                isManager: action.user.isManager,
                isSupervisor: action.user.isSupervisor,
                isSystemManager: action.user.isSystemManager,
                loggedIn: true,
                loggingIn: false,
                managerRole: action.user.managerRole,
                nameFamily: action.user.nameFamily,
                nameGiven: action.user.nameGiven,
                nameInfix: action.user.nameInfix,
                needsSupervisor: action.user.needsSupervisor,
                organizationHasGeneralPractitionerOrSupervisor:
                    action.user.organizationHasGeneralPractitionerOrSupervisor,
                organizationId: action.user.organizationId,
                organizationType: action.user.organizationType,
                profession: action.user.profession,
                profilePictureUrl: action.user.profilePictureUrl,
                selectedEmploymentId: action.user.selectedEmploymentId,
                servicePermissions: action.user.servicePermissions,
                token: action.user.token,
            };
        case AuthenticationType.authenticationLogout:
            return {
                failed: false,
                isChecking: false,
                loggedIn: false,
                loggingIn: false,
            };
        case AuthenticationType.authenticationCheckingFailed:
            return {
                failed: false,
                isChecking: false,
                loggedIn: false,
                loggingIn: false,
            };
        case AuthenticationType.authenticationChecking:
            return {
                failed: false,
                isChecking: true,
                loggedIn: false,
                loggingIn: false,
            };
        case AuthenticationType.authenticationUpdateProfilePicture:
            return {
                ...state,
                profilePictureUrl: action.profilePictureUrl,
            };
        case AuthenticationType.authenticationUpdateAreEmploymentTaxesDetailsRequired:
            return {
                ...state,
                areEmploymentTaxesDetailsRequired: action.areEmploymentTaxesDetailsRequired,
            };
        default:
            return state;
    }
}

export const rootReducer: redux.Reducer<IAuthenticationState, IAuthenticationAction> = authentication;
